import type React from "react";

import { useQueryClient } from "react-query";
import styled from "styled-components";
import invariant from "ts-invariant";

import { getConversationMessagesQueryKey } from "api/services/messaging/hooks/useGetConversationMessages";
import { queryKeyPrefix } from "api/services/messaging/hooks/useGetConversations";
import { useProfileContext } from "contexts/ProfileContext";
import { BookingForm } from "routes/messages/components/BookingForm";

import { useMessagesSearchParams } from "../../hooks/useMessagesSearchParams";

import { BookingFormV1 } from "./components/BookingFormV1";

const DRAWER_WIDTH = "400px";

interface BookingFormPanelProps {
  patientId: number;
}

export const BookingFormPanel: React.FC<BookingFormPanelProps> = ({ patientId }) => {
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const { closePanel } = useMessagesSearchParams();

  invariant(profile, "Profile should be defined");

  const hasBookingV2 = profile.therapist_profile?.feature_flags?.includes("BOOKING_V2");

  const handleCloseCallback = () => {
    closePanel("book");
    queryClient.invalidateQueries(queryKeyPrefix);
    queryClient.invalidateQueries(getConversationMessagesQueryKey({ userID: profile.id, patientID: patientId }));
  };

  if (!hasBookingV2) {
    return <BookingFormV1 patientId={patientId} closeCallback={handleCloseCallback} />;
  }

  return <StyledBookingForm closeCallback={handleCloseCallback} />;
};

const StyledBookingForm = styled(BookingForm)`
  max-height: 100%;

  ${({ theme }) => theme.aboveBreakpoint} {
    min-width: ${DRAWER_WIDTH};
  }
`;
